import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    if (this.dataset.productVariantsSize === '1') return;

    link.addEventListener('click', this.navigateToProduct);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.productCardLink.removeEventListener('click', this.navigateToProduct);
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    // console.log('handleVariantSelected', event);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * INFO: SSNT - Transformed into public (removed `#`) because of `ProductCardAddOn` extension
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  handleVariantUpdate(event) {
    // console.log('handleVariantUpdate', event);

    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.updateProductThumb(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.#updateProductForm(event);
    this.#updateWishlistToggle(event);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelector(`product-price [ref='priceContainer']`);
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Re-renders the featured image.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  async updateProductThumb(event) {
    if (!this.thumb) throw new Error('No product thumb found');

    const newProductThumbFigure = event.detail.data.html.querySelector('[data-product-thumb] figure');
    if (!newProductThumbFigure) throw new Error('No new product thumb found');

    const morphThumb = () => morph(this.thumbFigure, newProductThumbFigure);

    // If the variant wasn't fetched, and so it was cached and returned, there's
    // no need to show the loader, since the morph is immediate
    if (!event.detail.fetched) {
      morphThumb();
      return;
    }

    const show = () => {
      img.removeEventListener('load', show);

      window.requestAnimationFrame(() => {
        window.loaderHide(this.thumb.querySelectorAll('.sn_loader__wr'));
      });
    }

    const img = this.thumb.querySelector('img');
    const oldImageId = img.dataset.imageId;
    img.addEventListener('load', show);

    //

    window.loaderShow(this.thumb, { flat: true }).then(() => {
      morphThumb()

      // Force show the image if the ID isn't change, because no `load` event
      // will be triggered (since the image is basically not changed)
      if (oldImageId == img.dataset.imageId) show();
    });
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('[ref="productCardLink"]');

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Updates the quick add form
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductForm(event) {
    const productForm = this.querySelector('product-form-component');
    if (!productForm) return;

    const newProductForm = event.detail.data.html?.querySelector('product-form-component');
    if (!newProductForm) {
      productForm.remove();
      return;
    }

    morph(productForm, newProductForm);
  }

  /**
   * Updates the wishlist toggle
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateWishlistToggle(event) {
    const wishlistToggle = this.querySelector('.sn_wishlist_toggle');
    if (!wishlistToggle) return;

    const newWishlistToggle = event.detail.data.html?.querySelector('.sn_wishlist_toggle');
    const newNode = morph(wishlistToggle, newWishlistToggle, { childrenOnly: false });
    wishlistToggle.parentElement.replaceChild(newNode, wishlistToggle);
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Gets the thumb and figure elements
   * @returns {HTMLElement | null} The variant picker component.
   */
  get thumb() { return this.querySelector('[data-product-thumb]') };
  get thumbFigure() { return this.querySelector('[data-product-thumb] figure') };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof HTMLAnchorElement)) return;

    const productCardAnchor = event.target.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = event.target.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }
    history.replaceState({}, '', url.toString());
  };
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class ProductCardAddOn extends ProductCard {
  /**
   * Gets the checkbox used to add the additional product
   * @returns {HTMLInputElement | null} The variant picker component.
   */
  get addCheckbox() {
    return this.querySelector('input[type="checkbox"][name="id[]"]');
  }

  get addCheckboxLabel() {
    return this.querySelector('.form-check label');
  }

  /**
   * Handles the variant update event.
   * Do all the things that `ProductCard` does, plus update the variant add on
   * checkbox id
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  handleVariantUpdate = (event) => {
    super.handleVariantUpdate(event);

    //
    this.updateAddCheckbox(event);
  };

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateAddCheckbox(event) {
    const bottomPart = this.querySelector('.sn_card_add_on_bottom');
    if (!bottomPart) return;

    const newBottomPart = event.detail.data.html?.querySelector('.sn_card_add_on_bottom');
    if (!newBottomPart) {
      bottomPart.remove();
      return;
    }

    const formId = document.querySelector('.sn_main_product_add_ons').dataset.formId;
    const newInput = newBottomPart.querySelector('.form-check-input');
    if (formId && newInput) newInput.setAttribute('form', formId);

    morph(bottomPart, newBottomPart);
  }
}

if (!customElements.get('product-card-add-on')) {
  customElements.define('product-card-add-on', ProductCardAddOn);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {

}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
